import { useQuery } from '@tanstack/react-query';
import { RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';

/**
 * Reportes · Cerrados con pendientes (23-sep-2026). Antes era un correo
 * diario a Edwing (08:00); ahora se consulta aquí en vivo. Solo admin.
 * Embarques COMPLETADOS que aún tienen pendiente por facturar y/o
 * provisionar. Montos en USD (los de MTY convertidos al tipo de cambio
 * cotizado del booking); los negativos son ajustes/reversas.
 */

interface Fila {
  posicion: string;
  cliente: string;
  operativo: string;
  motivo: string;
}

export function CierresPendientesPage() {
  const q = useQuery({
    queryKey: ['cierres-pendientes'],
    queryFn: () =>
      api<{ filas: Fila[]; total: number; generadoAt: string }>(
        '/admin/reports/cierres-pendientes',
      ),
    refetchInterval: 5 * 60 * 1000,
  });
  const filas = q.data?.filas ?? [];

  return (
    <div className="px-3 sm:px-6 py-4 sm:py-6 space-y-4 max-w-6xl mx-auto">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h1 className="text-lg font-semibold">Reportes · Cerrados con pendientes</h1>
          <p className="text-sm text-ink-400">
            Embarques completados que aún tienen pendiente por facturar y/o provisionar.
            Montos en USD (los de MTY convertidos al tipo de cambio cotizado); los negativos
            son ajustes/reversas. Datos en vivo.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-ink-400">
            {q.data ? `${q.data.total} embarque${q.data.total === 1 ? '' : 's'}` : 'Cargando…'}
          </span>
          <Button variant="outline" size="sm" onClick={() => q.refetch()} disabled={q.isFetching}>
            <RefreshCw className={q.isFetching ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} />
          </Button>
        </div>
      </div>

      <div className="rounded-lg border border-ink-200 bg-surface-1 overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-surface-2 text-xs uppercase tracking-wider text-ink-400">
              <th className="px-3 py-2 text-left font-medium border-b border-ink-200">Embarque</th>
              <th className="px-3 py-2 text-left font-medium border-b border-ink-200">Cliente</th>
              <th className="px-3 py-2 text-left font-medium border-b border-ink-200">Operativo</th>
              <th className="px-3 py-2 text-left font-medium border-b border-ink-200">Motivo / alerta</th>
            </tr>
          </thead>
          <tbody>
            {filas.map((f) => (
              <tr key={f.posicion} className="border-b border-ink-100 hover:bg-surface-2/60">
                <td className="px-3 py-2 font-medium tabular whitespace-nowrap">{f.posicion}</td>
                <td className="px-3 py-2 max-w-[16rem] truncate" title={f.cliente}>
                  {f.cliente}
                </td>
                <td className="px-3 py-2 whitespace-nowrap">{f.operativo}</td>
                <td className="px-3 py-2 text-red-600 dark:text-red-400">{f.motivo}</td>
              </tr>
            ))}
            {filas.length === 0 && !q.isLoading && (
              <tr>
                <td colSpan={4} className="px-3 py-8 text-center text-ink-400">
                  Hoy no hay embarques cerrados con pendientes. ✓
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
